import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';

export interface ArenaCohost {
  id: string;
  arena_id: string;
  user_id: string;
  invited_by: string;
  status: 'pending' | 'accepted' | 'declined';
  created_at: string;
  accepted_at: string | null;
  profile?: {
    id: string;
    name: string;
    photo_url: string | null;
  };
}

export const useArenaCohosts = (arenaId: string | null) => {
  const { user } = useAuthContext();
  const [cohosts, setCohosts] = useState<ArenaCohost[]>([]);
  const [pendingInvites, setPendingInvites] = useState<ArenaCohost[]>([]);
  const [loading, setLoading] = useState(false);
  const [isCohost, setIsCohost] = useState(false);

  // Fetch cohosts for arena
  const fetchCohosts = useCallback(async () => {
    if (!arenaId) return;

    try {
      const { data, error } = await supabase
        .from('arena_cohosts')
        .select(`
          *,
          profile:profiles!arena_cohosts_user_id_fkey(id, name, photo_url)
        `)
        .eq('arena_id', arenaId);

      if (error) throw error;

      const allCohosts = (data || []).map(c => ({
        ...c,
        status: c.status as 'pending' | 'accepted' | 'declined',
        profile: c.profile as ArenaCohost['profile']
      }));

      setCohosts(allCohosts.filter(c => c.status === 'accepted'));
      setPendingInvites(allCohosts.filter(c => c.status === 'pending'));
      
      // Check if current user is a cohost
      if (user) {
        setIsCohost(allCohosts.some(c => c.user_id === user.id && c.status === 'accepted'));
      }
    } catch (error) {
      console.error('Error fetching cohosts:', error);
    }
  }, [arenaId, user]);

  // Subscribe to realtime changes
  useEffect(() => {
    if (!arenaId) return;

    fetchCohosts();

    const channel = supabase
      .channel(`arena-cohosts-${arenaId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'arena_cohosts',
          filter: `arena_id=eq.${arenaId}`,
        },
        () => {
          fetchCohosts();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [arenaId, fetchCohosts]);

  // Invite a cohost
  const inviteCohost = useCallback(
    async (userId: string) => {
      if (!arenaId || !user) return false;

      setLoading(true);
      try {
        const { error } = await supabase.from('arena_cohosts').insert({
          arena_id: arenaId,
          user_id: userId,
          invited_by: user.id,
          status: 'pending',
        });

        if (error) {
          if (error.code === '23505') {
            toast({
              title: 'Already invited',
              description: 'This user has already been invited as a co-host',
              variant: 'destructive',
            });
          } else {
            throw error;
          }
          return false;
        }

        toast({
          title: 'Invite sent! 📨',
          description: 'Co-host invitation has been sent',
        });
        return true;
      } catch (error) {
        console.error('Error inviting cohost:', error);
        toast({
          title: 'Error',
          description: 'Failed to send invitation',
          variant: 'destructive',
        });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [arenaId, user]
  );

  // Accept cohost invitation
  const acceptInvite = useCallback(
    async (inviteId: string) => {
      if (!user) return false;

      try {
        const { error } = await supabase
          .from('arena_cohosts')
          .update({
            status: 'accepted',
            accepted_at: new Date().toISOString(),
          })
          .eq('id', inviteId)
          .eq('user_id', user.id);

        if (error) throw error;

        toast({
          title: 'Welcome, co-host! 🎉',
          description: 'You now have moderation privileges',
        });
        return true;
      } catch (error) {
        console.error('Error accepting invite:', error);
        return false;
      }
    },
    [user]
  );

  // Decline cohost invitation
  const declineInvite = useCallback(
    async (inviteId: string) => {
      if (!user) return false;

      try {
        const { error } = await supabase
          .from('arena_cohosts')
          .update({ status: 'declined' })
          .eq('id', inviteId)
          .eq('user_id', user.id);

        if (error) throw error;

        toast({
          title: 'Invitation declined',
          description: 'You declined the co-host invitation',
        });
        return true;
      } catch (error) {
        console.error('Error declining invite:', error);
        return false;
      }
    },
    [user]
  );

  // Remove a cohost (by arena creator)
  const removeCohost = useCallback(
    async (cohostId: string) => {
      try {
        const { error } = await supabase
          .from('arena_cohosts')
          .delete()
          .eq('id', cohostId);

        if (error) throw error;

        toast({
          title: 'Co-host removed',
          description: 'The co-host has been removed from this arena',
        });
        return true;
      } catch (error) {
        console.error('Error removing cohost:', error);
        return false;
      }
    },
    []
  );

  // Get user's pending invitations across all arenas
  const getMyPendingInvites = useCallback(async () => {
    if (!user) return [];

    try {
      const { data, error } = await supabase
        .from('arena_cohosts')
        .select(`
          *,
          arena:live_arenas(id, name, topic),
          inviter:profiles!arena_cohosts_invited_by_fkey(id, name, photo_url)
        `)
        .eq('user_id', user.id)
        .eq('status', 'pending');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching pending invites:', error);
      return [];
    }
  }, [user]);

  return {
    cohosts,
    pendingInvites,
    loading,
    isCohost,
    inviteCohost,
    acceptInvite,
    declineInvite,
    removeCohost,
    getMyPendingInvites,
    refetch: fetchCohosts,
  };
};
